import React from "react";
import { Link } from "react-router-dom";
import BrandMark from "./BrandMark";
import MegaMenu from "./MegaMenu";

const HOME_LINKS = [
  { href: "#packages", label: "Packages", testid: "nav-packages" },
  { href: "#pricing", label: "Pricing", testid: "nav-pricing" },
  { href: "#faq", label: "FAQ", testid: "nav-faq" },
  { href: "#testimonials", label: "Reviews", testid: "nav-testimonials" },
];

// Reusable nav link sets per page type.
export const PRODUCT_LINKS = [
  { href: "#features", label: "Inside", testid: "nav-features" },
  { href: "#reserve", label: "Get It", testid: "nav-reserve" },
  { href: "#faq", label: "FAQ", testid: "nav-faq" },
  { href: "#testimonials", label: "Reviews", testid: "nav-testimonials" },
];

export const AUDIT_HUB_LINKS = [
  { href: "#features", label: "What's Included", testid: "nav-features" },
  { href: "#audit-packages", label: "Packages", testid: "nav-audit-packages" },
  { href: "#faq", label: "FAQ", testid: "nav-faq" },
  { href: "#testimonials", label: "Reviews", testid: "nav-testimonials" },
];

export const AUDIT_DETAIL_LINKS = [
  { href: "#includes", label: "What's Included", testid: "nav-includes" },
  { href: "#reserve", label: "Book", testid: "nav-reserve" },
  { href: "#faq", label: "FAQ", testid: "nav-faq" },
  { href: "#testimonials", label: "Reviews", testid: "nav-testimonials" },
];

/**
 * Sticky glass nav bar.
 * "Book Call" navigates to /book-a-call (audit packages hub).
 * `links` prop lets each page provide its own section anchors so the nav stays
 * relevant per page (Workshop/Vault/etc don't have a #pricing section, for example).
 */
export default function Nav({ onBookCall, links = HOME_LINKS }) {
  // onBookCall kept for backwards compatibility but unused — Book Call now navigates.
  void onBookCall;
  return (
    <nav
      data-testid="site-nav"
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#050505]/70 border-b border-white/5"
    >
      {/* Sticky top banner with phone number */}
      <div
        data-testid="nav-top-banner"
        className="border-b border-gold/15"
        style={{ background: "linear-gradient(90deg, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0.08) 50%, rgba(212,175,55,0.18) 100%)" }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-8 md:h-9 flex items-center justify-center md:justify-between text-[10px] md:text-[11px] text-white/80">
          <span className="hidden md:inline-flex items-center gap-1.5 uppercase tracking-[0.18em] font-semibold text-gold/85">
            <span className="w-1.5 h-1.5 rounded-full bg-[#7CE3A5] animate-pulse" />
            Open · Mon-Fri 9-6 ET
          </span>
          <a
            href="tel:+18887550507"
            data-testid="nav-phone-banner"
            className="inline-flex items-center gap-2 uppercase tracking-[0.18em] font-semibold text-white hover:text-gold transition-colors"
            aria-label="Call Bow Tie Kreative at 888-755-0507"
          >
            <svg className="w-3 h-3 text-gold" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M2.3 4.6c0-1.3 1-2.4 2.3-2.4h2c1 0 1.8.7 2 1.6l.6 2.4c.2.8-.1 1.6-.8 2L7 9.1c1 2 2.6 3.6 4.6 4.6l.9-1.4c.4-.7 1.2-1 2-.8l2.4.6c.9.2 1.6 1 1.6 2v2c0 1.3-1.1 2.3-2.4 2.3C8.1 18.4 1.6 11.9 2.3 4.6z"/>
            </svg>
            <span>(888) 755-0507</span>
          </a>
          <span className="hidden md:inline-block text-[10px] tracking-[0.18em] uppercase text-white/45">
            Talk to a real human →
          </span>
        </div>
      </div>

      {/* Main nav row */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="brand-link">
          <BrandMark size={36} testid="nav-brand-mark" />
          <span className="hidden sm:block font-display text-lg tracking-tight">
            Bow Tie <span className="bk-gold-text">Kreative</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8 text-sm text-white/70">
          {links.map((l) => (
            <a
              key={l.href + l.label}
              href={l.href}
              className="hover:text-white transition-colors"
              data-testid={l.testid}
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/book-a-call"
            data-testid="nav-book-call"
            className="text-xs uppercase tracking-[0.2em] font-semibold px-5 py-2.5 rounded-full bk-shimmer text-black hover:scale-[1.03] transition-transform"
          >
            Book Call
          </Link>
          <MegaMenu />
        </div>
      </div>
    </nav>
  );
}
